/* eslint-disable react/prop-types */
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@egaranti/components";
import { zodResolver } from "@hookform/resolvers/zod";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import productService from "@/services/productService";

import {
  formatPriceInput,
  formatPriceValue,
  parsePriceInput,
} from "@/lib/priceInput";

import SearchSelect from "./searchSelect";

import * as z from "zod";

/**
 * "Satış Ekle" ilk adımı: **Ana Kategori + Kategori + Ürün Fiyatı**.
 *
 * - İkisinde de ekleme YOK: yalnızca merchant'ın tanımlı kategorileri seçilebilir.
 *   Kategori, seçilen ana kategoriye bağlı olarak yüklenir.
 * - Marka ve Ürün Adı bu adımda sorulmuyor; plan seçiminden sonraki adımda
 *   (müşteri bilgilerinin üstünde) isteniyor. Bu adım yalnızca plan eşleştirmesi için
 *   gerekli olanı topluyor: kategori zinciri + fiyat.
 * - Fiyat alanı Türkçe biçimde otomatik gruplanır (15.000,50) — bkz. @/lib/priceInput.
 *
 * Eski sürümde bu adımda "Ek Hizmet Türü" dropdown'ı vardı: ürünle ilgisiz planlar
 * da seçilebiliyor ve fiyat eşleşmeyince satış hata veriyordu. Artık plan, fiyat ve
 * kategoriye göre eşleşen listeden seçiliyor (bkz. planPicker).
 */
function ProductInfoForm({ onSubmit, onReset, defaultValues, locked, loading }) {
  const { t } = useTranslation();

  const [mainCategories, setMainCategories] = useState([]);
  const [mainCategoriesLoading, setMainCategoriesLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const formSchema = useMemo(
    () =>
      z.object({
        mainCategoryId: z
          .string()
          .min(
            1,
            t("extendedWarranty.add.mainCategoryRequired", "Ana kategori seçiniz"),
          ),
        // Kategori merchant'ın o ana kategorisinde tanımlı değilse boş kalabilir;
        // zorunluluk aşağıda liste doluysa uygulanıyor (superRefine yerine burada
        // tutulamıyor çünkü kural forma değil yüklenen listeye bağlı).
        categoryId: z.string(),
        productPrice: z
          .string()
          .min(
            1,
            t("extendedWarranty.add.productPriceRequired", "Ürün fiyatı giriniz"),
          )
          .refine((value) => (parsePriceInput(value) || 0) > 0, {
            message: t(
              "extendedWarranty.add.productPriceInvalid",
              "Geçerli bir ürün fiyatı giriniz",
            ),
          }),
      }),
    [t],
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      mainCategoryId: defaultValues?.mainCategoryId
        ? String(defaultValues.mainCategoryId)
        : "",
      categoryId: defaultValues?.categoryId ? String(defaultValues.categoryId) : "",
      productPrice: formatPriceValue(defaultValues?.productPrice),
    },
  });

  // Taslaktan devam ederken ürün bilgisi bu form mount olduktan SONRA geliyor
  // (draft isteği asenkron). useForm defaultValues'ı yalnızca mount'ta okuduğu
  // için formu elle senkronlamazsak taslakta kategori/fiyat boş kalıyor.
  // null gelince sıfırlamıyoruz: "Düzenle" productInfo'yu null yapıyor ve girilen
  // değerlerin ekranda kalması gerekiyor.
  useEffect(() => {
    if (!defaultValues) return;
    form.reset({
      mainCategoryId: defaultValues.mainCategoryId
        ? String(defaultValues.mainCategoryId)
        : "",
      categoryId: defaultValues.categoryId ? String(defaultValues.categoryId) : "",
      productPrice: formatPriceValue(defaultValues.productPrice),
    });
  }, [defaultValues, form]);

  const mainCategoryId = form.watch("mainCategoryId");

  // Kategori listesi PRODUCTS_VIEW yetkisi istiyor; yetkisiz kullanıcıda 403 dönüyor.
  // Hatayı yutup boş liste göstermek "veri yok" gibi görünüyordu.
  const describeError = useCallback(
    (err, fallbackKey, fallbackText) =>
      err?.response?.status === 403
        ? t(
            "extendedWarranty.add.productsForbidden",
            "Ürün listesi için yetkiniz yok. Ayarlar → Personeller'den bu kullanıcıya Ürünler → Görüntüleme yetkisi verilmeli.",
          )
        : t(fallbackKey, fallbackText),
    [t],
  );

  useEffect(() => {
    let cancelled = false;
    setMainCategoriesLoading(true);
    productService
      .getMainCategories()
      .then((res) => {
        if (!cancelled) setMainCategories(res?.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setMainCategories([]);
        setLoadError(
          describeError(
            err,
            "extendedWarranty.add.categoriesLoadFailed",
            "Kategoriler yüklenemedi.",
          ),
        );
      })
      .finally(() => !cancelled && setMainCategoriesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [describeError]);

  // Kategoriler ana kategoriye bağlı. Taslaktan devam ederken de çalışır: ana kategori
  // form.reset ile geldiğinde bu effect tetiklenir ve seçili kategorinin adı çözülür.
  useEffect(() => {
    if (!mainCategoryId) {
      setCategories([]);
      return undefined;
    }
    let cancelled = false;
    setCategoriesLoading(true);
    productService
      .getCategories(mainCategoryId)
      .then((res) => {
        if (!cancelled) setCategories(res?.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setCategories([]);
        setLoadError(
          describeError(
            err,
            "extendedWarranty.add.categoriesLoadFailed",
            "Kategoriler yüklenemedi.",
          ),
        );
      })
      .finally(() => !cancelled && setCategoriesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [mainCategoryId, describeError]);

  const nameOf = (list, id) =>
    list.find((item) => String(item.id) === String(id))?.name || "";

  /**
   * Kategori zorunlu — ama yalnızca merchant'ın o ana kategoride kategorisi varsa.
   * Hiç yoksa satışı bloke etmiyoruz: product-api kategori boş gelirse seviyeyi ana
   * kategori adıyla türetiyor (akışın ilk sürümündeki davranış).
   */
  const handleSubmit = (values) => {
    if (categories.length > 0 && !values.categoryId) {
      form.setError("categoryId", {
        message: t("extendedWarranty.add.categoryRequired", "Kategori seçiniz"),
      });
      return;
    }

    onSubmit({
      mainCategoryId: values.mainCategoryId,
      mainCategoryName: nameOf(mainCategories, values.mainCategoryId),
      categoryId: values.categoryId || null,
      categoryName: nameOf(categories, values.categoryId),
      // Tutar her yerde sayı olarak taşınır; görünen biçim yalnızca bu alanda.
      productPrice: parsePriceInput(values.productPrice),
    });
  };

  return (
    <section className="formBox">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-medium text-[#101828]">
            {t("extendedWarranty.add.productInfoTitle", "Ürün Bilgisi")}
          </h2>
          {/* Locize'da önceki anahtarların metni duruyor ve fallback'i eziyor; metin
              her değiştiğinde yeni anahtar açılıyor. */}
          <p className="my-2 text-sm text-[#667085]">
            {t(
              "extendedWarranty.add.productInfoDescriptionCategoryFirst",
              'Kategori ve ürün fiyatını girip "İlerle" dediğinizde eşleşen paketler listelenir. Marka, ürün adı ve müşteri bilgileri sonraki adımda istenir.',
            )}
          </p>
        </div>
        {locked && (
          <Button type="button" variant="secondaryGray" onClick={onReset}>
            {t("extendedWarranty.add.edit", "Düzenle")}
          </Button>
        )}
      </div>

      {loadError && (
        <div className="mb-5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {loadError}
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="mainCategoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("extendedWarranty.add.mainCategoryLabel", "Ana Kategori")} *
                  </FormLabel>
                  <FormControl>
                    <SearchSelect
                      value={nameOf(mainCategories, field.value)}
                      items={mainCategories.map((category) => ({
                        key: category.id,
                        name: category.name,
                      }))}
                      loading={mainCategoriesLoading}
                      disabled={locked}
                      allowCreate={false}
                      placeholder={t(
                        "extendedWarranty.add.mainCategoryPlaceholder",
                        "Ana kategori seçiniz",
                      )}
                      emptyHint={t(
                        "extendedWarranty.add.noCategories",
                        "Tanımlı kategori yok. Ürünler ekranından kategori oluşturabilirsiniz.",
                      )}
                      onSelect={(item) => {
                        field.onChange(String(item.key));
                        // Ana kategori değişti: alt seçim artık geçersiz.
                        form.setValue("categoryId", "");
                        setCategories([]);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="categoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("extendedWarranty.add.categoryLabel", "Kategori")} *
                  </FormLabel>
                  <FormControl>
                    <SearchSelect
                      value={nameOf(categories, field.value)}
                      items={categories.map((category) => ({
                        key: category.id,
                        name: category.name,
                      }))}
                      loading={categoriesLoading}
                      disabled={locked || !mainCategoryId}
                      allowCreate={false}
                      placeholder={
                        mainCategoryId
                          ? t(
                              "extendedWarranty.add.categoryPlaceholder",
                              "Kategori seçiniz",
                            )
                          : t(
                              "extendedWarranty.add.selectMainCategoryFirst",
                              "Önce ana kategori seçiniz",
                            )
                      }
                      emptyHint={t(
                        "extendedWarranty.add.noCategoriesForMainCategory",
                        "Bu ana kategoride tanımlı kategori yok. Ürünler ekranından ekleyebilirsiniz.",
                      )}
                      onSelect={(item) => field.onChange(String(item.key))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="productPrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("extendedWarranty.add.productPriceLabel", "Ürün Fiyatı")} *
                  </FormLabel>
                  <FormControl>
                    {/* type="number" değil: binlik ayıracı gösterebilmek için metin
                        alanı gerekiyor. Mobil klavye inputMode ile sayısal açılır. */}
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="15.000,00"
                      disabled={locked}
                      {...field}
                      onChange={(event) =>
                        field.onChange(formatPriceInput(event.target.value))
                      }
                    />
                  </FormControl>
                  <FormDescription>
                    {t(
                      "extendedWarranty.add.productPriceDescriptionFormatted",
                      "Ek hizmet paketleri bu fiyata göre eşleştirilir. Binlik ayıracı otomatik eklenir, kuruş için virgül kullanın.",
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {!locked && (
            <div className="flex justify-end">
              <Button type="submit" disabled={loading}>
                {t("extendedWarranty.add.proceed", "İlerle")}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </section>
  );
}

export default ProductInfoForm;
